import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, MessageSquare, Layers, Circle, Users, FileText, Clock, ChevronRight as ChevronRightIcon, Sun, Moon } from 'lucide-react'
import { apiClient } from '../services/api'
import type { ShowcaseData, ShowcaseCrew } from '../types'
import { EnterWorkspaceIcon } from './icons/EnterWorkspaceIcon'
import { InteractiveHeroMockup } from './InteractiveHeroMockup'
import { WorkspaceThemeShowcase } from './WorkspaceThemeShowcase'
import { WechatQRCodeModal } from './WechatQRCodeModal'
import { ensureReadingFonts } from '../utils/fontLoader'

const WechatIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.14.045c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.269-.03-.406-.03zm-2.344 3.12c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.969-.982z"/>
  </svg>
)

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect() } },
      { threshold: 0.15 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return { ref, visible }
}

interface LandingPageProps {
  onLogin: () => void
  isAuthenticated?: boolean
}

interface CoreFeature {
  id: string
  title: string
  subtitle: string
  description: string
  highlights: string[]
  mediaPath: string
  posterPath: string
  mediaHint: string
  icon: typeof Briefcase
}

const CORE_FEATURES: CoreFeature[] = [
  {
    id: 'agent',
    title: '智能 Agent 员工',
    subtitle: '一句话下达目标，团队自动协作执行',
    description:
      '从信息检索、结构搭建、文案润色到自动配图生成，多个 Agent 按流程协同推进。你只管定方向，不再被工具细节困住。',
    highlights: ['可直接选择预设团队开工', '多角色并行处理复杂任务', '关键节点可中途插话调整'],
    mediaPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-agent.mp4',
    posterPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-agent-poster.svg',
    mediaHint: '从运行团队到确认交互卡片，再到右侧成果生成的 6 到 8 秒无声视频。',
    icon: Briefcase,
  },
  {
    id: 'template',
    title: '精美模板预览',
    subtitle: '写作、排版、视觉一体化输出',
    description:
      '为不同平台预置高质量模板，实时预览成品样式。发布前就能确认结构、封面和阅读节奏，减少返工。',
    highlights: ['模板风格统一且可复用', '支持内容与视觉同步预览', '输出结果可直接进入发布环节'],
    mediaPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-template.mp4',
    posterPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-template-poster.svg',
    mediaHint: '从输入 / 唤起菜单，到预览手机端排版的 6 到 8 秒演示。',
    icon: Layers,
  },
  {
    id: 'distribution',
    title: '便捷分发平台',
    subtitle: '内容完成后，一次操作同步分发',
    description:
      '在同一工作区管理发布目标、平台配置和分发记录。减少重复粘贴，避免不同平台切换造成的效率损耗。',
    highlights: ['统一管理多个发布渠道', '发布链路清晰可追踪', '支持团队内交付与分享协同'],
    mediaPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-distribution.mp4',
    posterPath: 'https://res.flowdraft.cn/static/frontend/landing/feature-distribution-poster.svg',
    mediaHint: '点击一键发布、勾选平台并完成成功状态，结尾停 1 秒。',
    icon: MessageSquare,
  },
]

export function LandingPage({ onLogin, isAuthenticated }: LandingPageProps) {
  const [showQRCode, setShowQRCode] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [stats, setStats] = useState<{ user_count: number; task_count: number } | null>(null)
  const [showcase, setShowcase] = useState<ShowcaseData | null>(null)
  const [expandedCrew, setExpandedCrew] = useState<string | null>(null)

  useLayoutEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0)
    }

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    resetScroll()
    const frameId = window.requestAnimationFrame(resetScroll)

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [])

  useEffect(() => {
    void ensureReadingFonts()
  }, [])

  useEffect(() => {
    apiClient.getPlatformStats().then(setStats).catch(() => {
      setStats(null)
    })
    apiClient.getShowcase().then(setShowcase).catch(() => {})
  }, [])

  return (
    <div className={`min-h-screen font-sans overflow-x-hidden transition-colors duration-500 landing-page ${isDark ? 'lp-dark' : ''}`}
      style={{ backgroundColor: 'var(--lp-bg)', color: 'var(--lp-text)' }}
    >
      {/* Nav */}
      <nav
        className="fixed w-full z-50 backdrop-blur-md border-b transition-colors duration-500"
        style={{ backgroundColor: 'var(--lp-nav-bg)', borderColor: 'var(--lp-border)' }}
      >
        <div className="max-w-[1300px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded flex items-center justify-center transition-colors duration-500"
              >
                <img
                  src="/favicon-32x32.png"
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-6 object-contain"
                />
              </div>
              <span className="font-semibold text-lg tracking-tight">FlowMore</span>
            </div>
            <Link
              to="/docs"
              className="hidden md:inline-flex text-sm font-medium transition-colors"
              style={{ color: 'var(--lp-text-2)' }}
            >
              文档
            </Link>
            <Link
              to="/tools"
              className="hidden md:inline-flex text-sm font-medium transition-colors"
              style={{ color: 'var(--lp-text-2)' }}
            >
              工具
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* WeChat QR code entry */}
            <button
              type="button"
              onClick={() => setShowQRCode(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 cursor-pointer border"
              style={{
                borderColor: 'var(--lp-border)',
                color: 'var(--lp-text-2)',
                backgroundColor: 'var(--lp-surface)',
              }}
              title="微信公众号"
              aria-label="打开微信公众号二维码"
            >
              <WechatIcon size={14} />
            </button>

            {/* Dark / Light toggle */}
            <button
              onClick={() => setIsDark(prev => !prev)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 cursor-pointer border"
              style={{
                borderColor: 'var(--lp-border)',
                color: 'var(--lp-text-2)',
                backgroundColor: 'var(--lp-surface)',
              }}
              title={isDark ? '切换亮色模式' : '切换暗色模式'}
              aria-label={isDark ? '切换亮色模式' : '切换暗色模式'}
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </button>

            {isAuthenticated ? (
              <Link
                to="/app"
                className="text-sm font-medium px-5 py-2 rounded-lg transition-all shadow-sm inline-flex items-center gap-1.5"
                style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
              >
                进入工作区
                <EnterWorkspaceIcon />
              </Link>
            ) : (
              <button
                onClick={onLogin}
                className="text-sm font-medium px-5 py-2 rounded-lg transition-all shadow-sm"
                style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
              >
                登录 / 注册
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 md:pt-36 pb-20 md:pb-24 px-6 max-w-[1400px] mx-auto text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full border shadow-sm mb-6 animate-fade-in transition-colors duration-500"
          style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
        >
          <span className="text-xs md:text-sm font-medium tracking-[0.01em]" style={{ color: 'var(--lp-text-2)' }}>AI 主编团队已就绪</span>
        </div>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-normal leading-[1.04] tracking-[-0.03em] mb-8 animate-slide-in">
          这一刻起， <br />
          你的身份是主编
        </h1>
        <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8 md:mb-10 lp-body-text animate-slide-in" style={{ color: 'var(--lp-text-2)' }}>
          FlowMore（拂墨）为你配备一支 <strong style={{ color: 'var(--lp-text)' }}>"即插即用" 的 AI 专家团队</strong>。<br />
          从全网信息搜集、多语言撰稿到视觉设计，<br className="md:hidden" />
          你只需下达指令，剩下的交给 Agent。
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 animate-slide-in">
          {isAuthenticated ? (
            <Link
              to="/app"
              className="w-full md:w-auto px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 shadow-lg flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
            >
              进入工作区
              <EnterWorkspaceIcon className="w-[18px] h-[18px] flex-shrink-0" />
            </Link>
          ) : (
            <button
              onClick={onLogin}
              className="w-full md:w-auto px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 shadow-lg flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
            >
              开始免费试用
              <EnterWorkspaceIcon className="w-[18px] h-[18px] flex-shrink-0" />
            </button>
          )}
        </div>

        {stats && (
          <div className="mt-16 md:mt-[72px] flex items-center justify-center gap-16 md:gap-28 animate-fade-in">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl border flex items-center justify-center shadow-sm transition-colors duration-500"
                style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
              >
                <Users size={18} style={{ color: 'var(--lp-text)' }} />
              </div>
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-mono font-semibold tabular-nums">
                  {stats.user_count.toLocaleString()}
                </div>
                <div className="text-xs font-mono font-medium tracking-wide" style={{ color: 'var(--lp-text-3)' }}>创作者加入</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl border flex items-center justify-center shadow-sm transition-colors duration-500"
                style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
              >
                <FileText size={18} style={{ color: 'var(--lp-text)' }} />
              </div>
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-mono font-semibold tabular-nums">
                  {stats.task_count.toLocaleString()}
                </div>
                <div className="text-xs font-mono font-medium tracking-wide" style={{ color: 'var(--lp-text-3)' }}>任务已完成</div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Product Preview (dedicated screen) */}
      <section className="py-20 md:py-32 px-6 max-w-[1400px] mx-auto">
        <div className="max-w-[1300px] mx-auto w-full animate-slide-in px-1 relative">
          <div className="hidden md:block">
            <InteractiveHeroMockup />
          </div>
          <div className="md:hidden">
            <img
              src="https://res.flowdraft.cn/static/frontend/landing/hero-editor-mac.png"
              alt="FlowMore 完整编辑区界面预览（Mac 设备示意）"
              className="w-full h-auto rounded-2xl border shadow-2xl shadow-black/[0.08] transition-colors duration-500"
              style={{ borderColor: 'var(--lp-border)' }}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section
        className="py-28 md:py-40 transition-colors duration-500"
        style={{ backgroundColor: 'var(--lp-bg)' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-20 md:mb-24">
            <p className="text-sm font-medium tracking-[0.02em] mb-3" style={{ color: 'var(--lp-text-3)' }}>核心能力</p>
            <h2 className="text-4xl md:text-5xl font-serif font-normal mb-4">解决你最关心的三件事</h2>
            <p style={{ color: 'var(--lp-text-2)' }} className="max-w-2xl mx-auto lp-body-text">
              把繁琐的重复工作交给 AI。FlowMore 帮你真正做到「会干活、出精品、发得快」。
            </p>
          </div>

          <div className="space-y-24 md:space-y-32">
            {CORE_FEATURES.map((feature, index) => {
              const FeatureIcon = feature.icon
              return (
                <div key={feature.id} className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-5 transition-colors duration-500"
                      style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
                    >
                      <FeatureIcon size={14} style={{ color: 'var(--lp-text)' }} />
                      <span className="text-xs font-medium tracking-[0.01em]" style={{ color: 'var(--lp-text-2)' }}>{feature.title}</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-serif font-normal mb-3">{feature.subtitle}</h3>
                    <p className="lp-body-text mb-5" style={{ color: 'var(--lp-text-2)' }}>{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.highlights.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--lp-text-feature)' }}>
                          <span
                            className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: 'var(--lp-text)' }}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <FeatureImageSlot
                    className={index % 2 === 1 ? 'lg:order-1' : ''}
                    src={feature.mediaPath}
                    poster={feature.posterPath}
                    title={feature.title}
                    hint={feature.mediaHint}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </section>


      {/* Workspace Theme Showcase */}
      <WorkspaceThemeShowcase />

      {/* Crew Showcase Section */}
      {showcase && showcase.crews.length > 0 && (
        <CrewShowcase
          crews={showcase.crews}
          expandedCrew={expandedCrew}
          onToggleCrew={(id) => setExpandedCrew(expandedCrew === id ? null : id)}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
        />
      )}

      {/* Roadmap Section */}
      <section className="py-28 md:py-40 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-20 md:mb-24">
          <h2 className="text-4xl md:text-5xl font-serif font-normal mb-4">产品路线图</h2>
          <p className="lp-body-text" style={{ color: 'var(--lp-text-2)' }}>我们正在构建的未来</p>
        </div>

        <div className="space-y-12">
          {/* Phase 1 */}
          <div className="relative pl-8 border-l-2" style={{ borderColor: 'var(--lp-border)' }}>
            <div className="absolute -left-[9px] top-0">
              <Circle size={16} style={{ color: 'var(--lp-text-3)', backgroundColor: 'var(--lp-bg)' }} />
            </div>
            <div className="mb-2">
              <span
                className="text-xs font-medium tracking-[0.02em] px-2 py-1 rounded transition-colors duration-500"
                style={{ color: 'var(--lp-text-2)', backgroundColor: 'var(--lp-badge)' }}
              >
                阶段 1
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">内容生产平台</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--lp-text-2)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>定时任务</strong>：每日日报自动发布</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>数据收集</strong>：收集各平台发布数据，精细化运营</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>知识管理</strong>：浏览器插件快速添加资料、作品标签分类、图谱聚类</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>多平台发布</strong>：一键授权、直接发布</span>
              </li>
            </ul>
          </div>

          {/* Phase 2 */}
          <div className="relative pl-8 border-l-2" style={{ borderColor: 'var(--lp-border)' }}>
            <div className="absolute -left-[9px] top-0">
              <Circle size={16} style={{ color: 'var(--lp-text-3)', backgroundColor: 'var(--lp-bg)' }} />
            </div>
            <div className="mb-2">
              <span
                className="text-xs font-medium tracking-[0.02em] px-2 py-1 rounded transition-colors duration-500"
                style={{ color: 'var(--lp-text-2)', backgroundColor: 'var(--lp-badge)' }}
              >
                阶段 2
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">个人 IP 管控</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--lp-text-2)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>人设学习</strong>：爆款内容分析、风格建模</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>合规检测</strong>：一键检测、修改建议</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>多平台适配</strong>：同一内容、一键适配</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>商业风控</strong>：广告合规、翻车预警</span>
              </li>
            </ul>
          </div>

          {/* Phase 3 */}
          <div className="relative pl-8 border-l-2" style={{ borderColor: 'var(--lp-border)' }}>
            <div className="absolute -left-[9px] top-0">
              <Circle size={16} style={{ color: 'var(--lp-text-3)', backgroundColor: 'var(--lp-bg)' }} />
            </div>
            <div className="mb-2">
              <span
                className="text-xs font-medium tracking-[0.02em] px-2 py-1 rounded transition-colors duration-500"
                style={{ color: 'var(--lp-text-2)', backgroundColor: 'var(--lp-badge)' }}
              >
                阶段 3
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">小红书深度支持</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--lp-text-2)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>浏览器自动化</strong>：自动发布、模拟操作</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>图片模版</strong>：HTML → 图片渲染</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>数据分析</strong>：笔记回传、爆款分析</span>
              </li>
            </ul>
          </div>

          {/* Phase 4 */}
          <div className="relative pl-8 border-l-2" style={{ borderColor: 'var(--lp-border)' }}>
            <div className="absolute -left-[9px] top-0">
              <Circle size={16} style={{ color: 'var(--lp-text-3)', backgroundColor: 'var(--lp-bg)' }} />
            </div>
            <div className="mb-2">
              <span
                className="text-xs font-medium tracking-[0.02em] px-2 py-1 rounded transition-colors duration-500"
                style={{ color: 'var(--lp-text-2)', backgroundColor: 'var(--lp-badge)' }}
              >
                阶段 4
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">高级交互</h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--lp-text-2)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>交互卡片</strong>：统一协议、确认即执行</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>首页对话</strong>：雇佣团队、动态招募</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--lp-text)' }}>•</span>
                <span><strong style={{ color: 'var(--lp-text)' }}>Crew 扩展</strong>：固定流程 + 创造性任务</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 text-center text-xs" style={{ color: 'var(--lp-text-3)' }}>
        {/* Social Icons */}
        <div className="flex justify-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => setShowQRCode(true)}
            className="w-9 h-9 rounded-full border flex items-center justify-center transition-colors group"
            style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)', color: 'var(--lp-text-3)' }}
            title="微信公众号"
            aria-label="打开微信公众号二维码"
          >
            <WechatIcon />
          </button>
        </div>
        <p className="mb-2">本网站所有资料归 flowdraft 和开发者版权所有</p>
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/docs"
            className="transition-colors"
            style={{ color: 'var(--lp-text-3)' }}
          >
            使用文档
          </Link>
          <button
            type="button"
            disabled
            className="bg-transparent border-0 p-0 cursor-not-allowed opacity-80"
            style={{ color: 'var(--lp-text-3)' }}
            title="敬请期待"
          >
            服务条款
          </button>
          <button
            type="button"
            disabled
            className="bg-transparent border-0 p-0 cursor-not-allowed opacity-80"
            style={{ color: 'var(--lp-text-3)' }}
            title="敬请期待"
          >
            隐私政策
          </button>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors opacity-80"
            style={{ color: 'var(--lp-text-3)' }}
          >
            沪ICP备2026012845号
          </a>
        </div>
      </footer>

      <WechatQRCodeModal open={showQRCode} onClose={() => setShowQRCode(false)} variant="landing" />
    </div>
  )
}

function FeatureImageSlot({
  src,
  poster,
  title,
  hint,
  className = '',
}: {
  src: string
  poster: string
  title: string
  hint: string
  spec?: string
  className?: string
}) {
  const isVideo = src.endsWith('.mp4') || src.endsWith('.webm')
  const [hasMediaError, setHasMediaError] = useState(false)
  const [hasPosterError, setHasPosterError] = useState(false)
  const showPoster = Boolean(poster) && !hasPosterError

  return (
    <div className={className}>
      <div
        className="group relative overflow-hidden rounded-[28px] border shadow-[0_24px_60px_rgba(55,53,47,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_72px_rgba(55,53,47,0.14)]"
        style={{ borderColor: 'var(--lp-border)', backgroundColor: 'var(--lp-surface)' }}
      >
        {!hasMediaError ? (
          isVideo ? (
            <video
              key={src}
              src={src}
              poster={showPoster ? poster : undefined}
              autoPlay
              loop
              muted
              playsInline
              className="w-full aspect-[16/10] object-cover"
              onError={() => setHasMediaError(true)}
            />
          ) : (
            <img
              src={src}
              alt={title}
              className="w-full aspect-[16/10] object-cover"
              onError={() => setHasMediaError(true)}
            />
          )
        ) : showPoster ? (
          <img
            src={poster}
            alt={`${title} poster`}
            className="w-full aspect-[16/10] object-cover"
            onError={() => setHasPosterError(true)}
          />
        ) : (
          <div className="aspect-[16/10] flex items-center justify-center px-6 text-center bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.95),_rgba(242,242,240,0.95))]">
            <div className="max-w-sm">
              <p className="text-sm font-semibold mb-2">{title} 宣传位待替换</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--lp-text-2)' }}>{hint}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Crew Showcase ─── */

function CrewShowcase({
  crews,
  expandedCrew,
  onToggleCrew,
  isAuthenticated,
  onLogin,
}: {
  crews: ShowcaseCrew[]
  expandedCrew: string | null
  onToggleCrew: (id: string) => void
  isAuthenticated?: boolean
  onLogin: () => void
}) {
  const { ref, visible } = useScrollReveal<HTMLElement>()

  const hasWorkflow = crews.filter((c) => c.steps.length > 0)
  const freePlay = crews.find((c) => c.steps.length === 0)

  return (
    <section
      ref={ref}
      className="py-28 md:py-40 px-6 transition-colors duration-500"
      style={{ backgroundColor: 'var(--lp-bg)' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className="text-center mb-20 md:mb-24 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border shadow-sm mb-5 transition-colors duration-500"
            style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
          >
            <Users size={14} style={{ color: 'var(--lp-text-2)' }} />
            <span className="text-xs md:text-sm font-medium tracking-[0.02em]" style={{ color: 'var(--lp-text-2)' }}>AI 团队</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-normal mb-4">
            选一支团队，下达指令，坐收成果
          </h2>
          <p className="max-w-2xl mx-auto lp-body-text" style={{ color: 'var(--lp-text-2)' }}>
            每支 Crew 都有明确的分工和流程。你只需选择场景，Agent 们会自动完成从采集、创作到发布的全部工作。
            <br />
            <span className="inline-block mt-2 text-sm font-medium tracking-[0.01em]" style={{ color: 'var(--lp-text-3)' }}>更多好用团队正在组建中</span>
          </p>
        </div>

        {/* Crew Cards Grid */}
        <div 
          className={`grid gap-6 mb-8 mx-auto ${
            hasWorkflow.length === 1 ? 'max-w-md grid-cols-1' :
            hasWorkflow.length === 2 ? 'max-w-4xl md:grid-cols-2' :
            'md:grid-cols-3'
          }`}
        >
          {hasWorkflow.map((crew, idx) => (
            <CrewCard
              key={crew.id}
              crew={crew}
              index={idx}
              parentVisible={visible}
              isExpanded={expandedCrew === crew.id}
              onToggle={() => onToggleCrew(crew.id)}
            />
          ))}
        </div>

        {/* Free Play Banner */}
        {freePlay && (
          <div
            className="transition-all duration-700"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transitionDelay: `${(hasWorkflow.length + 1) * 120}ms`,
            }}
          >
            <div
              className="relative overflow-hidden rounded-2xl border p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 transition-colors duration-500"
              style={{ borderColor: 'var(--lp-border)', backgroundColor: 'var(--lp-bg)' }}
            >
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border text-sm font-semibold"
                style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)', color: 'var(--lp-text)' }}
              >
                {getCrewMark(freePlay.name)}
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-semibold mb-1">{freePlay.name}</h3>
                <p className="text-sm lp-body-text" style={{ color: 'var(--lp-text-2)' }}>{freePlay.description}</p>
              </div>
              {isAuthenticated ? (
                <Link
                  to="/app"
                  className="flex-shrink-0 px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5"
                  style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
                >
                  进入工作区 <EnterWorkspaceIcon />
                </Link>
              ) : (
                <button
                  onClick={onLogin}
                  className="flex-shrink-0 px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5"
                  style={{ backgroundColor: 'var(--lp-cta)', color: 'var(--lp-cta-text)' }}
                >
                  免费试用 <EnterWorkspaceIcon />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/* ─── Single Crew Card ─── */

function CrewCard({
  crew,
  index,
  parentVisible,
  isExpanded,
  onToggle,
}: {
  crew: ShowcaseCrew
  index: number
  parentVisible: boolean
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div
      className="group p-6 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col hover:-translate-y-1 hover:shadow-[0_20px_42px_rgba(55,53,47,0.12)]"
      style={{
        backgroundColor: 'var(--lp-card)',
        borderColor: 'var(--lp-border)',
        opacity: parentVisible ? 1 : 0,
        transform: parentVisible ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: `${(index + 1) * 120}ms`,
        transitionProperty: 'opacity, transform, border-color, box-shadow, background-color',
        transitionDuration: '600ms',
      }}
      onClick={onToggle}
    >
      {/* Emoji + Name */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-xl border text-sm font-semibold"
            style={{ backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)', color: 'var(--lp-text)' }}
          >
            {getCrewMark(crew.name)}
          </span>
          <div>
            <h3 className="text-lg font-semibold leading-tight">{crew.name}</h3>
            {crew.estimated_time && (
              <div className="flex items-center gap-1 mt-1 text-xs font-medium tracking-[0.01em]" style={{ color: 'var(--lp-text-3)' }}>
                <Clock size={11} />
                <span>~{Math.ceil(crew.estimated_time / 60)} min</span>
              </div>
            )}
          </div>
        </div>
        <ChevronRightIcon
          size={16}
          className={`transition-transform duration-300 mt-1 ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-0.5'}`}
          style={{ color: 'var(--lp-text-3)' }}
        />
      </div>

      {/* Description */}
      <p className="text-sm lp-body-text mb-5 flex-1" style={{ color: 'var(--lp-text-2)' }}>{crew.description}</p>

      {/* Agent Avatars */}
      <div className="flex items-center -space-x-2">
        {crew.agents.map((agent, i) => (
          <div
            key={agent.id}
            className="relative group/avatar"
            style={{ zIndex: crew.agents.length - i }}
          >
            <img
              src={agent.avatar}
              alt={agent.name}
              className="w-8 h-8 rounded-full border-2 shadow-sm transition-transform duration-200 hover:scale-110 hover:z-10"
              style={{ borderColor: 'var(--lp-card)', backgroundColor: 'var(--lp-surface)' }}
            />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-stone-800 text-stone-50 text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none">
              {agent.name}
            </div>
          </div>
        ))}
        <span className="text-xs ml-3 font-medium tracking-[0.01em]" style={{ color: 'var(--lp-text-3)' }}>{crew.agents.length} 位 Agent</span>
      </div>

      {/* Expanded Agent Details */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? `${crew.agents.length * 72 + 16}px` : '0px', opacity: isExpanded ? 1 : 0 }}
      >
        <div className="mt-5 pt-4 border-t space-y-3" style={{ borderColor: 'var(--lp-border)' }}>
          {crew.agents.map((agent) => (
            <div key={agent.id} className="flex items-start gap-3">
              <img
                src={agent.avatar}
                alt={agent.name}
                className="w-9 h-9 rounded-full shadow-sm flex-shrink-0"
                style={{ backgroundColor: 'var(--lp-surface)' }}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{agent.name}</span>
                  <span
                    className="text-[10px] font-medium tracking-[0.01em] border rounded px-1.5 py-px"
                    style={{ color: 'var(--lp-text-3)', backgroundColor: 'var(--lp-surface)', borderColor: 'var(--lp-border)' }}
                  >
                    {agent.role}
                  </span>
                </div>
                <p className="text-xs leading-relaxed mt-0.5 line-clamp-2" style={{ color: 'var(--lp-text-2)' }}>{agent.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function getCrewMark(name: string) {
  const trimmed = name.trim()
  if (!trimmed) return 'AI'
  if (/^AI/i.test(trimmed)) return 'AI'
  return trimmed.slice(0, 1)
}
